import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Star,
  Heart,
  MessageCircle,
  MapPin,
  Phone,
  Mail,
  Globe,
  BadgeCheck,
  Share2,
  ChevronLeft,
  ChevronRight,
  Send,
  User,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VendorDetailPage = () => {
  const { id } = useParams();
  const { user, isAuthenticated, getAuthHeader } = useAuth();
  const [vendor, setVendor] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [isFavorited, setIsFavorited] = useState(false);
  const [messageOpen, setMessageOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 5, title: '', content: '' });

  useEffect(() => {
    fetchVendor();
    fetchReviews();
  }, [id]);

  const fetchVendor = async () => {
    try {
      const response = await axios.get(`${API}/vendors/${id}`);
      setVendor(response.data);
    } catch (error) {
      toast.error('Vendor not found');
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/vendors/${id}/reviews`);
      setReviews(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch reviews');
    }
  };

  const handleFavorite = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to save favorites');
      return;
    }
    try {
      if (isFavorited) {
        await axios.delete(`${API}/favorites/${id}`, { withCredentials: true });
      } else {
        await axios.post(`${API}/favorites/${id}`, {}, { withCredentials: true });
      }
      setIsFavorited(!isFavorited);
      toast.success(isFavorited ? 'Removed from favorites' : 'Added to favorites');
    } catch (error) {
      toast.error('Failed to update favorites');
    }
  };

  const handleSendMessage = async () => {
    if (!contactName.trim() || !contactEmail.trim() || !message.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSendingMessage(true);
    try {
      await axios.post(`${API}/contact-vendor`, {
        vendor_id: vendor.id,
        vendor_name: vendor.business_name,
        client_name: contactName,
        client_email: contactEmail,
        client_phone: contactPhone || null,
        message: message
      });
      
      toast.success('Inquiry sent successfully! We\'ll get back to you soon.');
      setMessage('');
      setContactName('');
      setContactEmail('');
      setContactPhone('');
      setMessageOpen(false);
    } catch (error) {
      toast.error('Failed to send inquiry. Please try again.');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      toast.error('Please login to submit a review');
      return;
    }

    try {
      await axios.post(`${API}/vendors/${id}/reviews`, {
        vendor_id: id,
        ...reviewForm
      }, { withCredentials: true });
      
      toast.success('Review submitted successfully!');
      setReviewForm({ rating: 5, title: '', content: '' });
      fetchReviews();
      fetchVendor();
    } catch (error) {
      toast.error('Failed to submit review');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-pink-200 border-t-pink-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Vendor not found</h2>
          <Link to="/vendors">
            <Button className="rounded-full">Back to Vendors</Button>
          </Link>
        </div>
      </div>
    );
  }

  const images = vendor.images?.length > 0 
    ? vendor.images 
    : ['https://images.unsplash.com/photo-1519741497674-611481863552?w=800'];

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50/50 to-white">
      {/* Image Gallery */}
      <div className="relative h-[50vh] lg:h-[60vh] bg-black">
        <img
          src={images[activeImage]}
          alt={vendor.business_name}
          className="w-full h-full object-cover"
        />
        
        {/* Navigation Arrows */}
        {images.length > 1 && (
          <>
            <button
              onClick={() => setActiveImage((prev) => (prev === 0 ? images.length - 1 : prev - 1))}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors"
              data-testid="prev-image-btn"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={() => setActiveImage((prev) => (prev === images.length - 1 ? 0 : prev + 1))}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors"
              data-testid="next-image-btn"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}

        {/* Image Thumbnails */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
          {images.map((img, idx) => (
            <button
              key={`thumb-${idx}-${img?.slice(-8) || idx}`}
              onClick={() => setActiveImage(idx)}
              className={`w-2.5 h-2.5 rounded-full transition-all ${
                idx === activeImage ? 'bg-white w-8' : 'bg-white/60 hover:bg-white/80'
              }`}
            />
          ))}
        </div>

        {/* Actions */}
        <div className="absolute top-4 right-4 flex gap-2">
          <button
            onClick={handleFavorite}
            className="w-12 h-12 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors"
            data-testid="vendor-favorite-btn"
          >
            <Heart className={`w-6 h-6 ${isFavorited ? 'text-pink-500 fill-pink-500' : 'text-gray-600'}`} />
          </button>
          <button className="w-12 h-12 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors">
            <Share2 className="w-6 h-6 text-gray-600" />
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Header */}
            <div>
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <h1 className="font-heading text-3xl lg:text-4xl font-bold flex items-center gap-2">
                    {vendor.business_name}
                    {vendor.is_verified && (
                      <BadgeCheck className="w-6 h-6 text-primary" />
                    )}
                  </h1>
                  <div className="flex items-center gap-4 mt-2 text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {vendor.cities && vendor.cities.length > 1 
                        ? vendor.cities.join(', ')
                        : vendor.city}
                    </span>
                    <Badge variant="secondary" className="bg-pink-50 text-pink-600 capitalize">
                      {vendor.category?.replace('-', ' ')}
                    </Badge>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 bg-pink-50 px-4 py-2 rounded-full">
                  <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
                  <span className="font-bold text-lg">{vendor.rating?.toFixed(1) || '5.0'}</span>
                  <span className="text-muted-foreground text-sm">({vendor.review_count} reviews)</span>
                </div>
              </div>
            </div>

            {/* About */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-3xl p-6 lg:p-8 border border-pink-100"
            >
              <h2 className="font-heading font-bold text-xl mb-4">About</h2>
              <p className="text-muted-foreground leading-relaxed">{vendor.description}</p>
            </motion.div>

            {/* Services */}
            {vendor.services?.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-3xl p-6 lg:p-8 border border-pink-100"
              >
                <h2 className="font-heading font-bold text-xl mb-4">Services</h2>
                <div className="flex flex-wrap gap-2">
                  {vendor.services.map((service, idx) => (
                    <Badge key={`svc-${service}-${idx}`} variant="secondary" className="bg-pink-50 text-pink-600 px-4 py-2 text-sm">
                      {service}
                    </Badge>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Reviews */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-3xl p-6 lg:p-8 border border-pink-100"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading font-bold text-xl">Reviews ({reviews.length})</h2>
              </div>

              {/* Review Form */}
              {isAuthenticated && (
                <form onSubmit={handleSubmitReview} className="mb-8 p-4 bg-pink-50 rounded-2xl">
                  <h3 className="font-medium mb-4">Write a Review</h3>
                  <div className="flex gap-1 mb-4">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                        className="transition-transform hover:scale-110"
                      >
                        <Star
                          className={`w-6 h-6 ${
                            star <= reviewForm.rating
                              ? 'text-amber-400 fill-amber-400'
                              : 'text-gray-300'
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                  <Input
                    placeholder="Review title"
                    value={reviewForm.title}
                    onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })}
                    className="mb-3 rounded-xl"
                    required
                  />
                  <Textarea
                    placeholder="Share your experience..."
                    value={reviewForm.content}
                    onChange={(e) => setReviewForm({ ...reviewForm, content: e.target.value })}
                    className="mb-3 rounded-xl"
                    rows={3}
                    required
                  />
                  <Button type="submit" className="btn-primary">
                    Submit Review
                  </Button>
                </form>
              )}

              {/* Reviews List */}
              <div className="space-y-6">
                {reviews.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No reviews yet. Be the first to review!</p>
                ) : (
                  reviews.map((review) => (
                    <div key={review.id} className="border-b border-pink-100 pb-6 last:border-0">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-medium">{review.user_name}</p>
                          <div className="flex gap-0.5">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={`star-${i}`}
                                className={`w-4 h-4 ${
                                  i < review.rating
                                    ? 'text-amber-400 fill-amber-400'
                                    : 'text-gray-200'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                        <span className="text-sm text-muted-foreground">
                          {new Date(review.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <h4 className="font-medium mb-1">{review.title}</h4>
                      <p className="text-muted-foreground">{review.content}</p>
                    </div>
                  ))
                )}
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              {/* Pricing Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-3xl p-6 border border-pink-100"
              >
                <div className="mb-6">
                  <span className="text-sm text-muted-foreground">Starting from</span>
                  <p className="text-3xl font-bold">
                    ${vendor.price_range_min?.toLocaleString() || '0'}
                    <span className="text-lg font-normal text-muted-foreground"> - </span>
                    ${vendor.price_range_max?.toLocaleString() || '0'}
                  </p>
                </div>

                <div className="space-y-3">
                  <Dialog open={messageOpen} onOpenChange={setMessageOpen}>
                    <DialogTrigger asChild>
                      <Button className="w-full btn-primary" data-testid="contact-vendor-btn">
                        <MessageCircle className="w-5 h-5 mr-2" />
                        Contact Vendor
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="rounded-3xl">
                      <DialogHeader>
                        <DialogTitle>Contact {vendor.business_name}</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 pt-4">
                        <div>
                          <label className="text-sm font-medium mb-1.5 block">Your Name *</label>
                          <Input
                            placeholder="Enter your name"
                            value={contactName}
                            onChange={(e) => setContactName(e.target.value)}
                            className="rounded-xl"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium mb-1.5 block">Your Email *</label>
                          <Input
                            type="email"
                            placeholder="your@email.com"
                            value={contactEmail}
                            onChange={(e) => setContactEmail(e.target.value)}
                            className="rounded-xl"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium mb-1.5 block">Phone (Optional)</label>
                          <Input
                            placeholder="(514) 555-0100"
                            value={contactPhone}
                            onChange={(e) => setContactPhone(e.target.value)}
                            className="rounded-xl"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium mb-1.5 block">Message *</label>
                          <Textarea
                            placeholder="Hi! I'm interested in your services for my wedding..."
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            rows={4}
                            className="rounded-xl"
                          />
                        </div>
                        <Button 
                          onClick={handleSendMessage} 
                          className="w-full btn-primary"
                          disabled={sendingMessage}
                        >
                          {sendingMessage ? (
                            <>Sending...</>
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-2" />
                              Send Inquiry
                            </>
                          )}
                        </Button>
                        <p className="text-xs text-gray-500 text-center">
                          Your inquiry will be forwarded to the vendor
                        </p>
                      </div>
                    </DialogContent>
                  </Dialog>

                  <Button
                    variant="outline"
                    onClick={handleFavorite}
                    className="w-full rounded-full border-pink-200 hover:bg-pink-50"
                    data-testid="save-vendor-btn"
                  >
                    <Heart className={`w-5 h-5 mr-2 ${isFavorited ? 'text-pink-500 fill-pink-500' : ''}`} />
                    {isFavorited ? 'Saved' : 'Save to Favorites'}
                  </Button>
                </div>
              </motion.div>

              {/* Contact Info */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-3xl p-6 border border-pink-100"
              >
                <h3 className="font-heading font-bold text-lg mb-4">Contact Info</h3>
                <div className="space-y-4">
                  {vendor.phone && (
                    <a href={`tel:${vendor.phone}`} className="flex items-center gap-3 text-muted-foreground hover:text-pink-500 transition-colors">
                      <div className="w-10 h-10 rounded-full bg-pink-50 flex items-center justify-center">
                        <Phone className="w-5 h-5 text-pink-500" />
                      </div>
                      {vendor.phone}
                    </a>
                  )}
                  {vendor.email && (
                    <a href={`mailto:${vendor.email}`} className="flex items-center gap-3 text-muted-foreground hover:text-pink-500 transition-colors">
                      <div className="w-10 h-10 rounded-full bg-pink-50 flex items-center justify-center">
                        <Mail className="w-5 h-5 text-pink-500" />
                      </div>
                      {vendor.email}
                    </a>
                  )}
                  {vendor.website && (
                    <a href={vendor.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-muted-foreground hover:text-pink-500 transition-colors">
                      <div className="w-10 h-10 rounded-full bg-pink-50 flex items-center justify-center">
                        <Globe className="w-5 h-5 text-pink-500" />
                      </div>
                      Website
                    </a>
                  )}
                  {vendor.address && (
                    <div className="flex items-center gap-3 text-muted-foreground">
                      <div className="w-10 h-10 rounded-full bg-pink-50 flex items-center justify-center">
                        <MapPin className="w-5 h-5 text-pink-500" />
                      </div>
                      {vendor.address}
                    </div>
                  )}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorDetailPage;
